import { pxToRem, toStringBloomLevel } from '@geneo2-web/shared-ui';
import { useTranslation } from 'react-i18next';
import {
  Drawer,
  Box,
  Typography,
  DrawerProps,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import {
  BloomStats,
  ConceptsData,
  DifficultyStats,
  QuestionStats,
  SessionMode,
  StrengthWeaknessItem,
} from './performanceDrawerData';
import { ProgressRing } from '../ProgressRing.tsx';
import { ProgressBar } from '../ProgressBar';
import { ConceptSection } from './components/ConceptSection';
import { BloomSection } from './components/BloomSection';
import { DifficultyBreakdownSection } from './components/DifficultyBreakdownSection';
import { StrengthWeaknessSection } from './components/StrengthWeaknessSection';
import CloseIcon from '@mui/icons-material/Close';
import {
  getMyPerformanceApi,
  IConceptSummary,
  IPerformanceSummary,
} from '../../api/learners';
import { getModuleMasteryApi, IModuleMasteryData } from '../../api/module';
import { formatUpiScore } from '../PerformancePopup';

const drawerStyles = {
  container: {
    width: { xs: '100vw', md: '440px' },
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f8fafc',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: `${pxToRem(20)} ${pxToRem(22)}`,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  },
  headerInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: pxToRem(12),
  },
  headerTitle: {
    fontSize: pxToRem(20),
    fontWeight: 800,
    color: '#fff',
  },
  headerSubtitle: {
    fontSize: pxToRem(12),
    color: 'rgba(255,255,255,0.8)',
    fontWeight: 500,
  },
  closeBtn: {
    color: '#fff',
    width: pxToRem(36),
    height: pxToRem(36),
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.1)',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.2)',
    },
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    paddingBottom: pxToRem(20),
  },
  tabsWrapper: {
    display: 'flex',
    padding: pxToRem(6),
    margin: `${pxToRem(16)} ${pxToRem(18)} 0`,
    borderRadius: pxToRem(12),
    backgroundColor: '#fff',
  },
  tab: {
    flex: 1,
    padding: `${pxToRem(10)} ${pxToRem(16)}`,
    borderRadius: pxToRem(9),
    fontSize: pxToRem(13),
    fontWeight: 700,
    cursor: 'pointer',
    textAlign: 'center',
    color: '#64748b',
  },
  tabActive: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#fff',
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: pxToRem(16),
    padding: pxToRem(18),
    margin: `${pxToRem(14)} ${pxToRem(18)} 0`,
    boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: pxToRem(8),
    marginBottom: pxToRem(14),
  },
  cardTitle: {
    fontSize: pxToRem(15),
    fontWeight: 700,
    color: '#1e293b',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4,1fr)',
    gap: pxToRem(8),
  },
  stat: {
    textAlign: 'center',
    padding: pxToRem(10),
    borderRadius: pxToRem(10),
    backgroundColor: '#f8fafc',
  },
  statValue: {
    fontSize: pxToRem(20),
    fontWeight: 800,
    color: '#1e293b',
  },
  statLabel: {
    fontSize: pxToRem(10),
    fontWeight: 700,
    color: '#94a3b8',
  },
  accuracyBar: {
    // height: pxToRem(8),
    // backgroundColor: '#e2e8f0',
    // borderRadius: pxToRem(8),
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    gap: pxToRem(4),
  },
  accuracyFill: {
    height: '100%',
    background: 'linear-gradient(90deg, #667eea, #764ba2)',
  },
};
// ── Mappers: IPerformanceSummary → sub-component props ──────────────

function toQuestionStats(s: IPerformanceSummary): QuestionStats {
  return {
    total: s.total_questions,
    correct: s.total_correct_questions,
    incorrect: s.total_incorrect_questions,
    skipped:
      (s.total_unattempted_questions || 0) + (s.total_skipped_questions || 0),
  };
}

const DIFFICULTY_MAP: Record<string, DifficultyStats['level']> = {
  LOW: 'easy',
  MEDIUM: 'medium',
  HIGH: 'hard',
};

function toDifficultyBreakdown(s: IPerformanceSummary): DifficultyStats[] {
  return Object.entries(s.difficulty_level_breakdown).map(([key, entry]) => ({
    level: DIFFICULTY_MAP[key] ?? 'easy',
    correct: entry.correct,
    total: entry.total,
    timeTaken: entry.total_time_taken,
  }));
}

function toBloomBreakdown(s: IPerformanceSummary): BloomStats[] {
  return Object.entries(s.bloom_level_breakdown)
    .map(([key, entry]) => {
      const label = toStringBloomLevel(key);
      if (!label) return null;
      return {
        level: label as BloomStats['level'],
        correct: entry.correct,
        total: entry.total,
      };
    })
    .filter((v): v is BloomStats => v !== null);
}

function toConceptsData(
  concepts: IConceptSummary[],
  mastery?: IModuleMasteryData
): ConceptsData {
  const masteryMap = new Map(
    mastery?.concept_states.map((c) => [c.concept_id, c.concept_status]) ?? []
  );
  const list = concepts.map((c) => {
    const status =
      masteryMap.get(c.concept_id) ||
      (c.total_questions > 0 ? 'developing' : 'not_assessed');
    return {
      name: c.title || c.name,
      icon: c.emoji || '📘',
      correct: c.total_correct_questions,
      total: c.total_questions,
      status: status == 'beginning' ? 'developing' : status,
    };
  });
  const mastered = list.filter((c) => c.status === 'mastered').length;
  const attempted = list.filter((c) => c.total > 0).length;
  return { noOfMastered: mastered, noOfAttempted: attempted, list };
}

function toStrengthsWeaknesses(concepts: IConceptSummary[]): {
  strengths: StrengthWeaknessItem[];
  weaknesses: StrengthWeaknessItem[];
} {
  const strengths: StrengthWeaknessItem[] = [];
  const weaknesses: StrengthWeaknessItem[] = [];

  for (const c of concepts) {
    if (c.total_questions === 0) continue;
    const pct = (c.total_correct_questions / c.total_questions) * 100;
    const item: StrengthWeaknessItem = {
      name: c.title || c.name,
      icon: c.emoji || '📘',
      accuracy:
        c.total_questions > 0
          ? formatUpiScore(c.total_correct_questions / c.total_questions)
          : 0,
      correct: c.total_correct_questions,
      incorrect: c.total_incorrect_questions,
    };
    if (pct >= 50) strengths.push(item);
    else weaknesses.push(item);
  }
  return { strengths, weaknesses };
}

function formatTime(s: number) {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

// ── Component types ──────────────────────────────────────────────────

interface IProps extends DrawerProps {
  open: boolean;
  onClose: () => void;
  module_id?: string;
  session_id?: string;
}
export const PerformanceDrawer = ({
  open,
  onClose,
  module_id,
  session_id,
}: IProps) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'overall' | 'session'>('overall');
  const [moduleMastery, setModuleMastery] = useState<
    IModuleMasteryData | undefined
  >(undefined);
  const [moduleSummary, setModuleSummary] = useState<
    IPerformanceSummary | undefined
  >(undefined);
  const [sessionSummary, setSessionSummary] = useState<
    IPerformanceSummary | undefined
  >(undefined);

  const [loading, setLoading] = useState<'loading' | 'error' | 'completed'>(
    'loading'
  );

  useEffect(() => {
    if (open) {
      fetchMyPerformance();
    } else {
      setLoading('loading');
    }
  }, [open]);

  const fetchMyPerformance = async () => {
    try {
      setLoading('loading');
      if (!module_id) {
        setLoading('completed');
        return;
      }
      const moduleMastery = (
        await getModuleMasteryApi({ module_id: module_id })
      )?.data;
      setModuleMastery(moduleMastery);
      const moduleSummary = await getMyPerformanceApi({
        module_id,
      });
      setModuleSummary(moduleSummary.results);
      const sessionSummary = await getMyPerformanceApi({
        module_id,
        session_id,
      });
      setSessionSummary(sessionSummary.results);
      setLoading('completed');
    } catch (err) {
      setLoading('error');
    }
  };

  // ── Derived data for active tab ──────────────────────────────────────

  const activeSummary = tab === 'overall' ? moduleSummary : sessionSummary;

  const questionStats = useMemo(
    () => (activeSummary ? toQuestionStats(activeSummary) : undefined),
    [activeSummary]
  );
  const accuracy = activeSummary?.session_accuracy ?? 0;
  const difficultyData = useMemo(
    () => (activeSummary ? toDifficultyBreakdown(activeSummary) : []),
    [activeSummary]
  );
  const bloomData = useMemo(
    () => (activeSummary ? toBloomBreakdown(activeSummary) : []),
    [activeSummary]
  );
  const conceptsData = useMemo(
    () =>
      activeSummary
        ? toConceptsData(activeSummary.concepts, moduleMastery)
        : undefined,
    [activeSummary, moduleMastery]
  );
  const { strengths, weaknesses } = useMemo(
    () =>
      activeSummary
        ? toStrengthsWeaknesses(activeSummary.concepts)
        : { strengths: [], weaknesses: [] },
    [activeSummary]
  );

  const sessionElapsed = sessionSummary
    ? formatTime(sessionSummary.total_time_taken)
    : '0:00';

  const renderContent = () => {
    return (
      <Box sx={drawerStyles.content}>
        {/* TABS */}
        <Box sx={drawerStyles.tabsWrapper}>
          <Box
            sx={{
              ...drawerStyles.tab,
              ...(tab === 'overall' ? drawerStyles.tabActive : {}),
            }}
            onClick={() => setTab('overall')}
          >
            {t('PAL_PD_TAB_OVERALL')}
          </Box>
          <Box
            sx={{
              ...drawerStyles.tab,
              ...(tab === 'session' ? drawerStyles.tabActive : {}),
            }}
            onClick={() => setTab('session')}
          >
            {t('PAL_PD_TAB_SESSION')}
          </Box>
        </Box>

        {tab === 'overall' && (
          <Box>
            {/* Mastery Score */}
            <Box sx={drawerStyles.card}>
              <Box sx={drawerStyles.cardHeader}>
                <Box display="flex" flexDirection="column" gap={1} width="100%">
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography>⭐</Typography>
                    <Typography color="#1e293b">
                      {t('PAL_PD_MASTERY_SCORE')}
                    </Typography>
                  </Box>
                  <Box
                    width="100%"
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    gap={2}
                  >
                    <ProgressRing
                      value={formatUpiScore(
                        moduleMastery?.progress?.upi_score_aggregate || 0
                      )}
                      diameter={140}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Questions */}
            <Box sx={drawerStyles.card}>
              <Box sx={{ ...drawerStyles.cardHeader, justifyContent: 'start' }}>
                <Typography>📝</Typography>
                <Typography sx={drawerStyles.cardTitle}>
                  {t('PAL_PD_QUESTIONS')}
                </Typography>
              </Box>
              <Box sx={drawerStyles.statsGrid}>
                {questionStats &&
                  Object.entries(questionStats).map(([key, value]) => {
                    const typedKey = key as keyof QuestionStats;
                    return (
                      <Stat
                        key={typedKey}
                        label={t(`PAL_PD_STAT_${typedKey.toUpperCase()}`)}
                        value={value}
                        statKey={typedKey}
                      />
                    );
                  })}
              </Box>
              <Box mt={4}>
                <Box sx={drawerStyles.accuracyBar}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="smallText">
                      {t('PAL_PD_ACCURACY')}
                    </Typography>
                    <Typography variant="smallText" color="#64748b">
                      {Math.round(accuracy)}%
                    </Typography>
                  </Box>
                  <ProgressBar valuePercent={accuracy} />
                </Box>
              </Box>
            </Box>
            {!!conceptsData?.list?.length && conceptsData?.list?.length > 0 && (
              <Box sx={drawerStyles.card}>
                <ConceptSection data={conceptsData} />
              </Box>
            )}
            {!!bloomData?.length && bloomData?.length > 0 && (
              <Box sx={drawerStyles.card}>
                <BloomSection data={bloomData} />
              </Box>
            )}
            {!!difficultyData?.length && difficultyData?.length > 0 && (
              <Box sx={drawerStyles.card}>
                <DifficultyBreakdownSection data={difficultyData} />
              </Box>
            )}
            <Box sx={drawerStyles.card}>
              <StrengthWeaknessSection
                strengths={strengths}
                weaknesses={weaknesses}
                mode="overall"
              />
            </Box>
          </Box>
        )}

        {/* SESSION TAB */}
        {tab === 'session' && (
          <Box>
            <Box sx={drawerStyles.card}>
              <Box sx={drawerStyles.cardHeader}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography>⚡</Typography>
                  <Typography sx={drawerStyles.cardTitle}>
                    {t('PAL_PD_CURRENT_SESSION')}
                  </Typography>
                </Box>
              </Box>

              {/* Session Timer */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: pxToRem(6),
                  padding: `${pxToRem(10)} ${pxToRem(14)}`,
                  background: '#f8fafc',
                  borderRadius: pxToRem(10),
                  marginBottom: pxToRem(14),
                }}
              >
                <Typography sx={{ fontSize: pxToRem(16), lineHeight: 1 }}>
                  ⏱
                </Typography>
                <Typography
                  sx={{
                    fontSize: pxToRem(20),
                    fontWeight: 800,
                    color: '#1e293b',
                    lineHeight: 1,
                  }}
                >
                  {sessionElapsed}
                </Typography>
                <Typography
                  sx={{
                    fontSize: pxToRem(12),
                    color: '#94a3b8',
                    fontWeight: 600,
                    marginLeft: pxToRem(4),
                  }}
                >
                  {t('PAL_PD_ELAPSED')}
                </Typography>
              </Box>

              <Box sx={drawerStyles.statsGrid}>
                {questionStats &&
                  Object.entries(questionStats).map(([key, value]) => {
                    const typedKey = key as keyof QuestionStats;
                    return (
                      <Stat
                        key={typedKey}
                        label={t(`PAL_PD_STAT_${typedKey.toUpperCase()}`)}
                        value={value}
                        statKey={typedKey}
                      />
                    );
                  })}
              </Box>

              <Box mt={4}>
                <Box sx={drawerStyles.accuracyBar}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="smallText">
                      {t('PAL_PD_SESSION_ACCURACY')}
                    </Typography>
                    <Typography variant="smallText" color="#64748b">
                      {Math.round(accuracy)}%
                    </Typography>
                  </Box>
                  <ProgressBar valuePercent={accuracy} />
                </Box>
              </Box>
            </Box>

            {!!conceptsData?.list?.length && conceptsData?.list?.length > 0 && (
              <Box sx={drawerStyles.card}>
                <ConceptSection data={conceptsData} />
              </Box>
            )}
            {!!bloomData?.length && bloomData?.length > 0 && (
              <Box sx={drawerStyles.card}>
                <BloomSection data={bloomData} />
              </Box>
            )}
            {!!difficultyData?.length && difficultyData?.length > 0 && (
              <Box sx={drawerStyles.card}>
                <DifficultyBreakdownSection data={difficultyData} />
              </Box>
            )}
            <Box sx={drawerStyles.card}>
              <StrengthWeaknessSection
                strengths={strengths}
                weaknesses={weaknesses}
                mode="session"
              />
            </Box>
          </Box>
        )}
      </Box>
    );
  };
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          boxShadow: '-8px 0 32px rgba(0,0,0,0.15)',
        },
      }}
    >
      <Box sx={drawerStyles.container}>
        {/* HEADER */}
        <Box sx={drawerStyles.header}>
          <Box sx={drawerStyles.headerInfo}>
            <Typography fontSize={28}>📊</Typography>
            <Box>
              <Typography sx={drawerStyles.headerTitle}>
                {t('PAL_PD_TITLE')}
              </Typography>
              <Typography sx={drawerStyles.headerSubtitle}>
                {tab === 'session'
                  ? t('PAL_PD_SUBTITLE_SESSION')
                  : t('PAL_PD_SUBTITLE_ALLTIME')}
              </Typography>
            </Box>
          </Box>
          <IconButton sx={drawerStyles.closeBtn} onClick={onClose}>
            <CloseIcon sx={{ fontSize: '16px' }} />
          </IconButton>
        </Box>

        {/* CONTENT */}
        {loading === 'loading' ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress sx={{ color: '#667eea' }} />
          </Box>
        ) : loading === 'error' ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 3,
            }}
          >
            <Typography
              sx={{
                color: '#667eea',
                fontSize: 14,
                fontWeight: 600,
                textAlign: 'center',
              }}
            >
              {t('PAL_PD_ERROR')}
            </Typography>
          </Box>
        ) : (
          renderContent()
        )}
      </Box>
    </Drawer>
  );
};

const Stat = ({
  label,
  value,
  statKey = 'total',
}: {
  label: string;
  value: any;
  statKey?: keyof QuestionStats;
}) => {
  const stylesByType = {
    total: {
      bg: '#f8fafc',
      valueColor: '#0f172a',
    },
    correct: {
      bg: '#f0fdf4',
      valueColor: '#16a34a',
    },
    incorrect: {
      bg: '#fef2f2',
      valueColor: '#dc2626',
    },
    skipped: {
      bg: '#eef2ff',
      valueColor: '#6366f1',
    },
    accuracy: {
      bg: '#f8fafc',
      valueColor: '#0f172a',
    },
    attempted: {
      bg: '#f8fafc',
      valueColor: '#0f172a',
    },
  } as const;

  const style = stylesByType[statKey] ?? stylesByType.total;

  return (
    <Box
      sx={{
        ...drawerStyles.stat,
        backgroundColor: style.bg,
      }}
    >
      <Typography
        sx={{
          fontSize: pxToRem(20),
          fontWeight: 800,
          color: style.valueColor,
          fontFamily: 'Poppins',
        }}
      >
        {value || 0}
      </Typography>

      <Typography
        sx={{
          fontSize: pxToRem(10),
          fontWeight: 700,
          color: '#64748b', // ✅ ALWAYS SAME DARK GREY
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

const modeLabels: Record<SessionMode, string> = {
  warmup: 'PAL_PD_MODE_WARMUP',
  learning: 'PAL_PD_MODE_LEARNING',
  practice: 'PAL_PD_MODE_PRACTICE',
  challenge: 'PAL_PD_MODE_CHALLENGE',
};

export const SessionModeBadge = ({ mode }: { mode: SessionMode }) => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        fontSize: pxToRem(11),
        fontWeight: 700,
        padding: `${pxToRem(3)} ${pxToRem(10)}`,
        borderRadius: pxToRem(8),
        background: 'linear-gradient(135deg, #667eea, #764ba2)',
        color: '#fff',
        display: 'inline-flex',
        alignItems: 'center',
        lineHeight: 1,
        whiteSpace: 'nowrap',
      }}
    >
      {t(modeLabels[mode])}
    </Box>
  );
};
